import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { resignationAPI } from '../../services';
import { 
  FileText, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Calendar,
  User,
  MessageSquare,
  Eye
} from 'lucide-react';

const ManagerResignations = () => {
  const { user } = useAuth();
  const [resignations, setResignations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedResignation, setSelectedResignation] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [filter, setFilter] = useState('pending'); // Add filter state
  const [actionData, setActionData] = useState({
    action: '',
    comments: '',
    recommendedLastWorkingDate: ''
  });

  useEffect(() => {
    fetchTeamResignations();
  }, [filter]); // Re-fetch when filter changes

  const fetchTeamResignations = async () => {
    try {
      setLoading(true);
      const response = await resignationAPI.getTeamResignations();
      const allResignations = response.data.resignations || [];
      
      // Filter resignations based on selected filter
      let filtered = allResignations;
      if (filter === 'pending') {
        filtered = allResignations.filter(r => r.reporterAction?.status === 'pending');
      } else if (filter === 'actioned') {
        filtered = allResignations.filter(r => 
          r.reporterAction?.status === 'approved' || 
          r.reporterAction?.status === 'rejected'
        );
      }
      // 'all' shows everything
      
      setResignations(filtered);
    } catch (error) {
      console.error('Error fetching team resignations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleActionSubmit = async () => {
    if (!actionData.action) {
      alert('Please select an action');
      return;
    }

    if (!actionData.comments.trim()) {
      alert('Please provide comments');
      return;
    }

    try {
      await resignationAPI.reporterAction(selectedResignation._id, actionData);
      alert(`Resignation ${actionData.action} successfully`);
      setShowActionModal(false);
      setSelectedResignation(null);
      setActionData({ action: '', comments: '', recommendedLastWorkingDate: '' });
      fetchTeamResignations();
    } catch (error) {
      console.error('Error processing resignation:', error);
      alert(error.response?.data?.message || 'Failed to process resignation');
    }
  };

  const openActionModal = (resignation) => {
    setSelectedResignation(resignation);
    setShowActionModal(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Team Resignations</h1>
        <p className="text-gray-600">Review and action resignation requests from your team members</p>
      </div>

      {/* Filter Tabs */}
      <div className="mb-6 flex gap-2">
        <button
          onClick={() => setFilter('pending')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'pending'
              ? 'bg-yellow-600 text-white'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          Pending Review
        </button>
        <button
          onClick={() => setFilter('actioned')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'actioned'
              ? 'bg-blue-600 text-white'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          Actioned
        </button>
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'all'
              ? 'bg-gray-600 text-white'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          All History
        </button>
      </div>

      {resignations.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {filter === 'pending' ? 'No Pending Resignations' : 'No Resignations Found'}
          </h2>
          <p className="text-gray-600">
            {filter === 'pending' 
              ? 'There are no resignation requests pending your review.' 
              : 'No resignation history available for your team.'}
          </p>
        </div>
      ) : (
        <div className="grid gap-6">
          {resignations.map((resignation) => (
            <div key={resignation._id} className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <User className="w-8 h-8 mr-3" />
                    <div>
                      <h3 className="text-lg font-semibold">{resignation.employee.name}</h3>
                      <p className="text-sm opacity-90">
                        {resignation.employee.employeeId} • {resignation.employee.designation}
                      </p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    resignation.reporterAction?.status === 'pending' 
                      ? 'bg-white bg-opacity-20' 
                      : resignation.reporterAction?.status === 'approved'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {resignation.reporterAction?.status === 'pending' 
                      ? 'Pending Review' 
                      : resignation.reporterAction?.status === 'approved'
                      ? 'Approved'
                      : 'Rejected'}
                  </span>
                </div>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Type</p>
                    <p className="font-medium text-gray-900 capitalize">
                      {resignation.resignationType.replace(/_/g, ' ')}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Submitted Date</p>
                    <p className="font-medium text-gray-900">
                      {new Date(resignation.submittedDate).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Last Working Date</p>
                    <p className="font-medium text-gray-900">
                      {new Date(resignation.lastWorkingDate).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Notice Period</p>
                    <p className="font-medium text-gray-900">{resignation.noticePeriodDays} days</p>
                  </div>
                </div>

                <div className="mb-6">
                  <p className="text-sm text-gray-500 mb-2">Reason for Resignation</p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-900">{resignation.reason}</p>
                  </div>
                </div>

                {resignation.feedback && (
                  <div className="mb-6">
                    <p className="text-sm text-gray-500 mb-2">Additional Feedback</p>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-900">{resignation.feedback}</p>
                    </div>
                  </div>
                )}

                {/* Show reporter action status if already actioned */}
                {resignation.reporterAction?.status !== 'pending' && (
                  <div className={`mb-6 p-4 rounded-lg ${
                    resignation.reporterAction?.status === 'approved' 
                      ? 'bg-green-50 border border-green-200' 
                      : 'bg-red-50 border border-red-200'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      {resignation.reporterAction?.status === 'approved' ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600" />
                      )}
                      <span className={`font-semibold ${
                        resignation.reporterAction?.status === 'approved' 
                          ? 'text-green-900' 
                          : 'text-red-900'
                      }`}>
                        Already {resignation.reporterAction?.status === 'approved' ? 'Approved' : 'Rejected'}
                      </span>
                    </div>
                    {resignation.reporterAction?.comments && (
                      <p className="text-sm text-gray-700 ml-7">
                        Comments: {resignation.reporterAction.comments}
                      </p>
                    )}
                    {resignation.reporterAction?.actionDate && (
                      <p className="text-xs text-gray-600 ml-7 mt-1">
                        {new Date(resignation.reporterAction.actionDate).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}

                <div className="flex gap-3">
                  {resignation.reporterAction?.status === 'pending' ? (
                    <>
                      <button
                        onClick={() => {
                          setSelectedResignation(resignation);
                          setActionData({ ...actionData, action: 'approved' });
                          setShowActionModal(true);
                        }}
                        className="flex-1 bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center font-medium"
                      >
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Approve
                      </button>
                      <button
                        onClick={() => {
                          setSelectedResignation(resignation);
                          setActionData({ ...actionData, action: 'rejected' });
                          setShowActionModal(true);
                        }}
                        className="flex-1 bg-red-600 text-white py-3 px-4 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center font-medium"
                      >
                        <XCircle className="w-5 h-5 mr-2" />
                        Reject
                      </button>
                    </>
                  ) : (
                    <div className="flex-1 bg-gray-100 text-gray-600 font-semibold py-3 px-4 rounded-lg text-center">
                      Action Already Taken
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Action Modal */}
      {showActionModal && selectedResignation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">
                {actionData.action === 'approved' ? 'Approve' : 'Reject'} Resignation
              </h2>
              <button
                onClick={() => {
                  setShowActionModal(false);
                  setSelectedResignation(null);
                  setActionData({ action: '', comments: '', recommendedLastWorkingDate: '' });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Employee Details</h3>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">{selectedResignation.employee.name}</span> •{' '}
                  {selectedResignation.employee.employeeId} •{' '}
                  {selectedResignation.employee.designation}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Requested LWD: {new Date(selectedResignation.lastWorkingDate).toLocaleDateString()}
                </p>
              </div>

              {actionData.action === 'approved' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Recommended Last Working Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={actionData.recommendedLastWorkingDate}
                    onChange={(e) => setActionData({ ...actionData, recommendedLastWorkingDate: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Leave blank to accept the employee's requested date
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comments <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={actionData.comments}
                  onChange={(e) => setActionData({ ...actionData, comments: e.target.value })}
                  rows="4"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={
                    actionData.action === 'approved'
                      ? 'Provide your approval comments and any instructions...'
                      : 'Provide reason for rejection...'
                  }
                  required
                />
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium mb-1">Important</p>
                    <p>
                      {actionData.action === 'approved'
                        ? 'After your approval, this resignation will be forwarded to the admin team for final approval.'
                        : 'Rejecting this resignation will notify the employee. They can submit a new request if needed.'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={handleActionSubmit}
                  className={`flex-1 py-3 px-6 rounded-lg text-white font-medium transition-colors ${
                    actionData.action === 'approved'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  Confirm {actionData.action === 'approved' ? 'Approval' : 'Rejection'}
                </button>
                <button
                  onClick={() => {
                    setShowActionModal(false);
                    setSelectedResignation(null);
                    setActionData({ action: '', comments: '', recommendedLastWorkingDate: '' });
                  }}
                  className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerResignations;
